import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { services } from "@/data/services";

const LOGO = "/brand/uppit-logo-creme.png";
const WHATSAPP =
  "https://wa.me/31641262267?text=" +
  encodeURIComponent("Hoi Tim, ik wil graag samenwerken met UPPIT.");

const links = [
  { label: "AI Scan", to: "/ai-scan" },
  { label: "Cases", to: "/cases" },
  { label: "Blog", to: "/blog" },
  { label: "Over ons", to: "/over-ons" },
  { label: "Contact", to: "/contact" },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdown(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setDropdown(false);
        setOpen(false);
      }
    };
    document.addEventListener("click", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdown(false);
  }, [location.pathname]);

  const dienstenActive = location.pathname.startsWith("/diensten");
  const headerSolid = solid || open;

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        open
          ? "bg-obsidian border-b border-white/15"
          : headerSolid
          ? "bg-obsidian/95 backdrop-blur-xl border-b border-white/15 shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
          : "bg-gradient-to-b from-obsidian/80 to-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-3.5 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="UPPIT — terug naar home" className="shrink-0">
          <img src={LOGO} alt="UPPIT logo" width="110" height="36" className="h-7 sm:h-8 w-auto select-none" draggable="false" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/" end data-testid="nav-link-home" className="relative group px-4 py-2 text-[15px] font-medium">
            {({ isActive }) => (
              <>
                <span className={`transition-colors ${isActive ? "text-white" : "text-white/85 group-hover:text-white"}`}>Home</span>
                <span
                  className={`absolute left-4 right-4 bottom-1 h-[2px] bg-electric-400 transition-all duration-300 ${
                    isActive ? "w-[calc(100%-2rem)]" : "w-0 group-hover:w-[calc(100%-2rem)]"
                  }`}
                />
              </>
            )}
          </NavLink>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdown((v) => !v)}
              data-testid="nav-link-diensten"
              aria-expanded={dropdown}
              className={`relative px-4 py-2 text-[15px] font-medium flex items-center gap-1 transition-colors ${
                dienstenActive ? "text-white" : "text-white/85 hover:text-white"
              }`}
            >
              Diensten
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdown ? "rotate-180" : ""}`} />
              {dienstenActive && <span className="absolute left-4 right-4 bottom-1 h-[2px] bg-electric-400" />}
            </button>
            <AnimatePresence>
              {dropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[640px] rounded-2xl border border-white/10 bg-obsidian-800 backdrop-blur-xl p-3 shadow-float"
                >
                  <Link
                    to="/diensten"
                    data-testid="nav-dropdown-overview"
                    className="flex items-center justify-between px-4 py-3 rounded-xl bg-electric/15 border border-electric/25 text-electric-200 text-[11px] font-mono uppercase tracking-[0.25em] mb-2 hover:bg-electric/20"
                  >
                    Overzicht alle diensten <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <div className="grid grid-cols-2 gap-1">
                    {services.map((s) => {
                      const Icon = s.icon;
                      return (
                        <Link
                          key={s.slug}
                          to={`/diensten/${s.slug}`}
                          data-testid={`nav-dropdown-${s.slug}`}
                          className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/5 group"
                        >
                          <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 grid place-items-center text-electric-300 group-hover:bg-electric group-hover:text-white group-hover:border-electric transition-colors shrink-0">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-display font-semibold text-[15px] text-ivory leading-tight">{s.title}</div>
                            <div className="text-xs text-white/45 leading-snug mt-0.5">{s.short}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="relative group px-4 py-2 text-[15px] font-medium"
            >
              {({ isActive }) => (
                <>
                  <span className={`transition-colors ${isActive ? "text-white" : "text-white/85 group-hover:text-white"}`}>{l.label}</span>
                  <span
                    className={`absolute left-4 right-4 bottom-1 h-[2px] bg-electric-400 transition-all duration-300 ${
                      isActive ? "w-[calc(100%-2rem)]" : "w-0 group-hover:w-[calc(100%-2rem)]"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/ai-scan"
            data-testid="nav-cta-aiscan"
            className="hidden sm:inline-flex items-center gap-2 bg-electric hover:bg-electric-600 text-white font-semibold text-sm rounded-full px-5 py-2.5 transition-all duration-300 hover:shadow-glow-blue"
          >
            Gratis AI Scan
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-white/15 text-ivory"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-obsidian shadow-float"
          >
            <div className="px-6 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
              <Link to="/" data-testid="mobile-nav-link-home" className="py-3 text-base text-white/90 hover:text-electric-300 border-b border-white/10">
                Home
              </Link>
              <details className="group">
                <summary
                  data-testid="mobile-nav-link-diensten"
                  className="py-3 text-base text-white/90 hover:text-electric-300 border-b border-white/10 cursor-pointer flex items-center justify-between"
                >
                  Diensten
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                </summary>
                <div className="pl-4 py-2 space-y-1 border-b border-white/10">
                  <Link to="/diensten" data-testid="mobile-nav-alle-diensten" className="block py-2 text-sm text-electric-300 font-semibold">Alle diensten →</Link>
                  {services.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/diensten/${s.slug}`}
                      data-testid={`mobile-nav-${s.slug}`}
                      className="block py-2 text-sm text-white/75 hover:text-electric-300"
                    >
                      {s.title}
                    </Link>
                  ))}
                </div>
              </details>
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  className="py-3 text-base text-white/90 hover:text-electric-300 border-b border-white/10"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/ai-scan"
                data-testid="mobile-nav-aiscan"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-electric text-white font-semibold rounded-full py-3"
              >
                Gratis AI Growth Scan
              </Link>
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-whatsapp"
                className="mt-2 inline-flex items-center justify-center gap-2 border border-white/15 text-ivory font-semibold rounded-full py-3"
              >
                WhatsApp Tim
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navigation;
